type SendDescription = (type: 'sfu-offer' | 'sfu-answer', sdp: string) => void

// Serialize SDP operations for one PC. The browser is the polite peer;
// the SFU keeps its own offer on glare because Pion cannot roll it back.
export function createSFUNegotiation(
  pc: RTCPeerConnection,
  send: SendDescription,
  isCurrent: () => boolean,
) {
  let operations = Promise.resolve()
  let pendingLocalOffer = false
  let pendingICE: RTCIceCandidateInit[] = []

  const enqueue = (operation: () => Promise<void>) => {
    const next = operations.then(async () => {
      if (isCurrent()) await operation()
    })
    // A failed operation must not poison the queue for future messages.
    operations = next.catch(() => {})
    return next
  }

  const flushICE = async () => {
    const candidates = pendingICE
    pendingICE = []
    for (const candidate of candidates) {
      if (!isCurrent()) return
      try {
        await pc.addIceCandidate(candidate)
      } catch (err) {
        console.warn('SFU buffered ICE candidate failed:', err)
      }
    }
  }

  const offerIfStable = async () => {
    if (!isCurrent() || !pendingLocalOffer || pc.signalingState !== 'stable') return
    const offer = await pc.createOffer()
    if (!isCurrent()) return
    await pc.setLocalDescription(offer)
    if (!isCurrent()) return
    pendingLocalOffer = false
    send('sfu-offer', pc.localDescription?.sdp || offer.sdp || '')
  }

  return {
    offer: () => enqueue(async () => {
      pendingLocalOffer = true
      await offerIfStable()
    }),

    answer: (sdp: string) => enqueue(async () => {
      // An answer to an offer rolled back on glare is no longer relevant.
      if (pc.signalingState !== 'have-local-offer') return
      await pc.setRemoteDescription({ type: 'answer', sdp })
      await flushICE()
      await offerIfStable()
    }),

    remoteOffer: (sdp: string) => enqueue(async () => {
      if (pc.signalingState === 'have-local-offer') {
        await pc.setLocalDescription({ type: 'rollback' })
        // Rollback must not lose the camera/screen changes we published.
        pendingLocalOffer = true
      }
      if (!isCurrent()) return
      await pc.setRemoteDescription({ type: 'offer', sdp })
      await flushICE()
      if (!isCurrent()) return
      const answer = await pc.createAnswer()
      if (!isCurrent()) return
      await pc.setLocalDescription(answer)
      if (!isCurrent()) return
      send('sfu-answer', pc.localDescription?.sdp || answer.sdp || '')
      await offerIfStable()
    }),

    candidate: (candidate: RTCIceCandidateInit) => enqueue(async () => {
      if (!pc.remoteDescription) {
        pendingICE.push(candidate)
        return
      }
      await pc.addIceCandidate(candidate)
    }),
  }
}
